import { AstroPayCardPayment, AstroPayCore, AstroPayFullCheckout } from '@astropay/payments-lib';
import { APAvailableLanguages, APEnvironment } from '@astropay/payments-lib/types';
import { useCheckout } from '@core/context/checkoutContext';
import { useMessage } from '@core/context/messageContext';
import { TAstroPayHttpError } from '@core/httpClient/types';
import { AstroPayI18n } from '@core/i18n';
import { ROUTES } from '@routes/paths';
import AmplitudeAnalytics from '@services/amplitude';
import { PaymentService } from '@services/payment';
import { EPaymentStatus, ERequestedPaymentMethod, TPaymentDetails } from '@services/payment/types';
import { RiskifiedService } from '@services/riskified';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import astropayLogo from '@assets/logos/astropay-logo.png';
import { APButton } from '@components/atoms/APButton';
import { APIcon } from '@components/atoms/APIcon';
import { Typography } from '@components/atoms/Typography';
import { OrderSummaryCard } from '@components/organisms/OrderSummaryCard';
import { PaymentPageSkeleton } from '@components/organisms/skeleton/PaymentPage';
import { EInternalTerm } from '@pages/PaymentStatus/types';
import { showAmount } from '@utils/currency';
import { buildPathWithParams, parseQueryData, validateUrlFormat } from '@utils/urls';

import { paymentsLibStyles } from './styles';
import { TEncodeData } from './type';

export const PaymentPage = () => {
  const { paymentExternalId } = useParams();
  const navigate = useNavigate();
  const { setMessage } = useMessage();
  const { setMerchantLogoUrl, setOnBack } = useCheckout();

  const [paymentDetails, setPaymentDetails] = useState<TPaymentDetails>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // TODO: temporary fix — remove once back action is config for merchant
  const isBombo = paymentDetails?.paymentInfo.merchant.name.toLowerCase() === 'bombo';
  const showGoBack =
    !isBombo &&
    (paymentDetails?.paymentInfo.merchant?.cancelUrl || paymentDetails?.paymentInfo.merchant?.url);

  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const paymentComponentRef = useRef<AstroPayFullCheckout | AstroPayCardPayment | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const encodedData = urlParams.get('data');

  const formatEnv = (env: APEnvironment | string) => {
    switch (env) {
      case 'tst':
      case 'test':
        return 'test' as APEnvironment;
      case 'stg':
      case 'staging':
      case 'sandbox':
        return 'sandbox';
      case 'prod':
      case 'production':
      default:
        return 'production';
    }
  };

  let appIdToken = 'unknown';
  let environment: APEnvironment = 'production';
  if (encodedData) {
    try {
      const parsedData = parseQueryData<TEncodeData>(encodedData);

      if (parsedData?.app_id) appIdToken = parsedData.app_id;
      if (parsedData?.environment) environment = formatEnv(parsedData.environment);
    } catch (err) {
      console.warn('Invalid base64 "data" param:', err);
    }
  }

  useEffect(() => {
    const initAstroPayLib = () => {
      AstroPayCore.init(appIdToken, {
        environment,
        styles: paymentsLibStyles,
        language: AstroPayI18n.getCurrent() as APAvailableLanguages,
        theme: 'light',
        tracking: true,
      });
    };

    const fetchPaymentDetails = async () => {
      try {
        // Handle Base64 query param "data");
        const response = await PaymentService.getPaymentDetails(paymentExternalId || '');
        setPaymentDetails(response);

        setMerchantLogoUrl(response.paymentInfo.merchant.logo || astropayLogo);
        RiskifiedService.init(response.paymentExternalId);

        // ------------amp event -------------
        AmplitudeAnalytics.hostedCheckoutImpression(
          response.paymentExternalId,
          response.paymentInfo.merchant.name,
        );
        // ------------amp event -------------

        const paymentStatus = response.paymentInfo.paymentStatus;

        switch (paymentStatus) {
          case EPaymentStatus.EXPIRED:
            setMessage({
              error: 'PAYMENT_EXPIRED',
              message: '',
              internalTerm: EInternalTerm.PAYMENT_EXPIRED,
            });
            navigate(
              buildPathWithParams(ROUTES.PAYMENT_STATUS, {
                paymentExternalId: paymentExternalId || '',
              }),
            );
            return;

          case EPaymentStatus.COMPLETED:
            setMessage({
              error: 'PAYMENT_COMPLETED',
              message: '',
              internalTerm: EInternalTerm.PAYMENT_COMPLETED,
            });
            navigate(
              buildPathWithParams(ROUTES.PAYMENT_STATUS, {
                paymentExternalId: paymentExternalId || '',
              }),
            );
            return;
        }

        if (paymentContainerRef.current) {
          paymentContainerRef.current.innerHTML = '';

          let paymentComponent;
          if (response.requestedPaymentMethod === ERequestedPaymentMethod.CARD) {
            paymentComponent = new AstroPayCardPayment({
              paymentExternalId: response.paymentExternalId,
              showPaymentSummary: false,
              onSuccess: () =>
                handleSuccessCallback(
                  response?.paymentInfo.merchant.successUrl ?? response?.paymentInfo.merchant.url,
                ),
            });
          } else {
            paymentComponent = new AstroPayFullCheckout({
              showTitle: false,
              paymentExternalId: response.paymentExternalId,
              showDescription: false,
              cardConfig: {
                showPaymentSummary: false,
              },
              onSuccess: () =>
                handleSuccessCallback(
                  response?.paymentInfo.merchant.successUrl ?? response?.paymentInfo.merchant.url,
                ),
            });
          }

          paymentComponent.render(paymentContainerRef.current);
          paymentComponentRef.current = paymentComponent;
        }
      } catch (error) {
        const err = error as TAstroPayHttpError;
        setMessage(err);

        navigate(
          buildPathWithParams(ROUTES.PAYMENT_STATUS, {
            paymentExternalId: paymentExternalId || '',
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    initAstroPayLib();
    fetchPaymentDetails();
  }, []);

  const handleSuccessCallback = (successUrl?: string) => {
    if (successUrl) window.open(validateUrlFormat(successUrl), '_self');
  };

  const onReturnToMerchant = useCallback(() => {
    const cancelUrl =
      paymentDetails?.paymentInfo.merchant.cancelUrl || paymentDetails?.paymentInfo.merchant.url;

    if (paymentDetails && cancelUrl) {
      // ------------amp event -------------
      AmplitudeAnalytics.hostedCheckoutPaymentCancelClicked(
        paymentDetails.paymentExternalId,
        paymentDetails.paymentInfo.merchant.name,
      );
      // ------------amp event -------------

      window.open(validateUrlFormat(cancelUrl), '_self');
    }
  }, [paymentDetails]);

  useEffect(() => {
    if (showGoBack) setOnBack(onReturnToMerchant);

    return () => setOnBack(undefined);
  }, [paymentDetails]);

  return (
    <div className="flex flex-col gap-4 w-full max-w-2xl mx-auto rounded-2xl py-4 px-1">
      {isLoading ? (
        <PaymentPageSkeleton />
      ) : (
        <>
          {/* Total */}
          <div className="flex flex-col mb-2 px-1">
            <Typography
              textKey="Checkout.total"
              className="text-[12px] text-[#8A908F] uppercase"
              weight={400}
            />

            <span className="flex items-baseline gap-1">
              <Typography
                text={showAmount({
                  value: paymentDetails?.order.paymentAmount.amount ?? 0,
                  currency: paymentDetails?.order.paymentAmount.currency ?? '',
                  hideCurrency: true,
                })}
                className="text-[38px] text-[#0F1514] leading-none"
                weight={600}
              />
              {paymentDetails?.order.paymentAmount.currency && (
                <Typography
                  text={paymentDetails?.order.paymentAmount.currency}
                  className="text-[12px] text-[#8A908F]"
                  weight={500}
                />
              )}
            </span>
          </div>

          {paymentDetails?.paymentInfo.expirationMessage && (
            <div className="flex flex-row w-full items-center gap-2 bg-white rounded-[16px] p-[14px]">
              <div className="py-1 bg-[#FDF5B4] rounded-lg flex items-center justify-center shrink-0 pl-2 pr-1">
                <APIcon img="timeLine" size={20} />
              </div>

              <div className="flex flex-col">
                <Typography
                  text={paymentDetails.paymentInfo.expirationMessage}
                  weight={500}
                  className="text-[14px] text-[#0F1514] leading-[1.2]"
                />

                <Typography
                  textKey="Checkout.completePaymentBeforeExpiry"
                  className="text-[12px] text-[#0F1514] leading-[1.2]"
                />
              </div>
            </div>
          )}
          {paymentDetails?.order.items && paymentDetails.order.items.length > 0 && (
            <div>
              <OrderSummaryCard
                items={paymentDetails.order.items}
                summary={paymentDetails.order.summary}
                currency={paymentDetails.order.paymentAmount.currency}
              />
            </div>
          )}
        </>
      )}

      <div
        className={`${isLoading && 'hidden'} flex flex-col items-center justify-center gap-2 bg-white rounded-[16px] p-[14px]`}
      >
        <div ref={paymentContainerRef} id="payment-container" className="w-full" />

        {!isLoading && showGoBack && (
          <APButton
            variant="tertiary"
            size="large"
            textKey="CommonMessages.payment.cancel"
            className="pr-5 text-[#0F1514]"
            onClick={onReturnToMerchant}
          />
        )}
      </div>
    </div>
  );
};
